import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SettingSmallText, SettingSubHeading } from '../settings.style';
import { NAMESPACE_TRANSLATION } from 'internationalise';
import {
  getAutomarkableLabel,
  getAutomarkedLabel,
  getMarkbookLabel,
  getUnitQuizLabel,
} from 'approot/shared/logic/content-label';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import {
  InquisitiveActivitiesContentStatus,
  InquisitiveActivitiesContentType,
} from 'global/graphql/scalars.type';
import useUserData from 'approot/shared/user/user-data';
import {
  ACTIVITIES_CONTENT_STATUS_PUBLISHED,
  ASSESSMENT_ACTIVITIES,
} from 'global/constants';
import Switch from 'approot/shared/switch/switch';
import ActivitiesPublishPermissionWarning from '../../permissions/activities-publish-permission-warning';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { getInteractiveContentLabel } from 'approot/activity-builder/activity-builder.utils';
import {
  DraftAssessmentActivities,
  DraftLessonActivities,
} from 'approot/activity-builder/activity-builder-draft.constants';
import SettingsPublishModal from './settings-publish-modal';
import SettingsPublishMinorModal from './settings-publish-minor-modal';
import SettingsPublishMajorModal from './setting-publish-major-modal';
import SettingsReviewChangesModal from './settings-review-changes-modal';
import SettingsPublishMinor from './settings-publish-minor';
import SettingsPublishMajor from './settings-publish-major';
import { WarningBanner } from 'approot/shared/design-system/banner/banner';
import IconAlert from 'approot/shared/icons/icon-alert';

type Props = {
  contentId: number;
  contentType: InquisitiveActivitiesContentType;
  status: InquisitiveActivitiesContentStatus;
  hasUnpublishedChanges: boolean;
  activities: DraftLessonActivities | DraftAssessmentActivities;
};

const SettingsPublish = ({
  contentId,
  contentType,
  status,
  hasUnpublishedChanges,
  activities,
}: Props) => {
  const { t } = useTranslation(NAMESPACE_TRANSLATION);
  const automarkableLabel = getAutomarkableLabel(t);
  const automarkedLabelLower = getAutomarkedLabel(t, { lower: true });
  const unitQuizLabel = getUnitQuizLabel(t);
  const unitQuizLabelLower = getUnitQuizLabel(t, { lower: true });
  const markbookLabel = getMarkbookLabel(t);
  const markbookLabelLower = getMarkbookLabel(t, { lower: true });

  const [createNewVersion, setCreateNewVersion] = useState<{
    description: string;
    isCreateNewVersion: boolean;
  }>({
    description: '',
    isCreateNewVersion: false,
  });

  const [isCheckingForChanges, setIsCheckingForChanges] =
    useState<boolean>(false);

  const { canPublishActivities } = useUserData();

  const {
    isVisible: isPublishMinorUpdateVisible,
    openModal: openPublishMinorUpdateModal,
    closeModal: closePublishMinorUpdateModal,
  } = useSimpleModalState();
  const {
    isVisible: isPublishMajorUpdateVisible,
    openModal: openPublishMajorUpdateModal,
    closeModal: closePublishMajorUpdateModal,
  } = useSimpleModalState();
  const {
    isVisible: isReviewChangesVisible,
    openModal: openReviewChangesModal,
    closeModal: closeReviewChangesModal,
  } = useSimpleModalState();

  const {
    isVisible: isPublishVisible,
    openModal: openPublishModal,
    closeModal: closePublishModal,
  } = useSimpleModalState();

  const {
    isVisible: isWarningVisible,
    openModal: openWarningModal,
    closeModal: closeWarningModal,
  } = useSimpleModalState();

  const isPublished = status === ACTIVITIES_CONTENT_STATUS_PUBLISHED;
  const isAssessment = contentType === ASSESSMENT_ACTIVITIES;
  // Assessments can now be marked as automarkable
  const storedIsAutomarkableStatus = useMemo(
    () =>
      isAssessment &&
      ('isAutomarkable' in activities ? !!activities.isAutomarkable : false),
    [activities, isAssessment]
  );
  const [isAutomarkable, setIsAutomarkable] = useState<boolean>(
    storedIsAutomarkableStatus
  );
  const automarkableDirty =
    isAssessment && isAutomarkable !== storedIsAutomarkableStatus;

  const contentTypeLabel = useMemo(
    () => getInteractiveContentLabel(contentType),
    [contentType]
  );

  const handleOpenReviewChangesModal = useCallback(() => {
    if (!canPublishActivities) {
      openWarningModal();
    } else {
      setIsCheckingForChanges(false);
      openReviewChangesModal();
    }
  }, [canPublishActivities, openReviewChangesModal, openWarningModal]);

  const handleOpenPublishMajorUpdateModal = useCallback(() => {
    if (!canPublishActivities) {
      openWarningModal();
    } else {
      setIsCheckingForChanges(false);
      openPublishMajorUpdateModal();
    }
  }, [canPublishActivities, openPublishMajorUpdateModal, openWarningModal]);

  const handleOpenPublishMinorUpdateModal = useCallback(() => {
    if (!canPublishActivities) {
      openWarningModal();
    } else {
      setIsCheckingForChanges(false);
      openPublishMinorUpdateModal();
    }
  }, [canPublishActivities, openPublishMinorUpdateModal, openWarningModal]);

  return (
    <>
      {isPublishVisible && (
        <SettingsPublishModal
          contentId={contentId}
          contentType={contentType}
          activities={activities}
          createNewVersion={createNewVersion.isCreateNewVersion}
          description={createNewVersion.description}
          isAutomarkable={isAssessment && isAutomarkable}
          onClose={closePublishModal}
        />
      )}

      {isWarningVisible && (
        <ActivitiesPublishPermissionWarning onClose={closeWarningModal} />
      )}

      {isPublishMinorUpdateVisible && (
        <SettingsPublishMinorModal
          triggerMinorUpdate={() => {
            setCreateNewVersion({
              description: '',
              isCreateNewVersion: false,
            });
            openPublishModal();
          }}
          closeModal={closePublishMinorUpdateModal}
        />
      )}

      {isPublishMajorUpdateVisible && (
        <SettingsPublishMajorModal
          triggerMajorUpdate={(description: string) => {
            setCreateNewVersion({
              description,
              isCreateNewVersion: true,
            });
            openPublishModal();
          }}
          closeModal={closePublishMajorUpdateModal}
        />
      )}

      {isReviewChangesVisible && (
        <SettingsReviewChangesModal
          contentId={contentId}
          contentType={contentType}
          onMinorUpdateSelected={openPublishMinorUpdateModal}
          onMajorUpdateSelected={openPublishMajorUpdateModal}
          closeModal={closeReviewChangesModal}
        />
      )}

      {!isPublished ? (
        <Box>
          <Box $marginBottom={8}>
            <PrimaryButton
              data-selector="btn-publish"
              type="button"
              onClick={() => {
                if (!canPublishActivities) {
                  openWarningModal();
                } else if (!isPublished) {
                  setCreateNewVersion({
                    description: '',
                    isCreateNewVersion: true,
                  });
                  openPublishModal();
                }
              }}
            >
              Publish {contentTypeLabel}
            </PrimaryButton>
          </Box>
          <SettingSmallText>
            Share your {contentTypeLabel} with the world for the first time.
            Once published, teachers will be able to find and assign it to
            students.
          </SettingSmallText>
        </Box>
      ) : isPublished && (hasUnpublishedChanges || automarkableDirty) ? (
        <>
          <Box $marginBottom={24}>
            <SettingsPublishMinor
              contentId={contentId}
              contentType={contentType}
              disabled={isCheckingForChanges}
              openReviewChangesModal={handleOpenReviewChangesModal}
              openPublishMinorUpdateModal={handleOpenPublishMinorUpdateModal}
              setIsCheckingForChanges={setIsCheckingForChanges}
            />
          </Box>

          <SettingsPublishMajor
            contentId={contentId}
            contentType={contentType}
            disabled={isCheckingForChanges}
            openReviewChangesModal={handleOpenReviewChangesModal}
            openPublishMajorUpdateModal={handleOpenPublishMajorUpdateModal}
            setIsCheckingForChanges={setIsCheckingForChanges}
          />
        </>
      ) : (
        <Box>
          <Box $marginBottom={8}>
            <PrimaryButton type="button" disabled>
              Publish {contentTypeLabel}
            </PrimaryButton>
          </Box>

          <SettingSmallText>
            No changes have been made to the content or Teaching Notes since
            this was last published.
          </SettingSmallText>
        </Box>
      )}
      {isAssessment && canPublishActivities && (
        <Box $marginBottom={24}>
          <Box
            $flexDirection="row"
            $alignItems="center"
            $justifyContent="space-between"
            $marginBottom={8}
          >
            <SettingSubHeading as="span">{automarkableLabel}</SettingSubHeading>
            <Switch
              state={isAutomarkable}
              ariaLabel={`Click to ${
                isAutomarkable ? 'not' : ''
              } create an ${automarkedLabelLower} ${unitQuizLabelLower} with the ${markbookLabelLower} attached`}
              onClick={() => setIsAutomarkable(prev => !prev)}
            />
          </Box>
          <WarningBanner $size="small">
            <Box
              $flexDirection="row"
              $alignItems="flex-start"
              $gap={8}
              $flexWrap="nowrap"
            >
              <Box>
                <IconAlert width={16} height={16} />
              </Box>
              <SettingSmallText marginBottom={0}>
                Turn on to create an {automarkedLabelLower} {unitQuizLabel} with
                the {markbookLabel} attached.
              </SettingSmallText>
            </Box>
          </WarningBanner>
        </Box>
      )}
    </>
  );
};

export default SettingsPublish;
